import { useState } from 'react'
import { X, ChevronRight } from 'lucide-react'
import { HELP_MODULES, type HelpModule } from './helpContent'

interface HelpPanelProps {
  open: boolean
  onClose: () => void
}

export function HelpPanel({ open, onClose }: HelpPanelProps) {
  // HELP_MODULES is statically populated and known non-empty in helpContent.ts.
  // The non-null assertion below mirrors that invariant for the type system.
  const firstModule = HELP_MODULES[0]!
  const [activeModuleId, setActiveModuleId] = useState<string>(firstModule.id)
  const activeModule: HelpModule = HELP_MODULES.find((m) => m.id === activeModuleId) ?? firstModule

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Slide-over panel */}
      <div className="relative ml-auto w-full max-w-3xl bg-slate-950 border-l border-slate-800 shadow-2xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">User Guide</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Everything you need to know about DebtOS
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — sidebar + content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Module sidebar */}
          <nav className="w-56 border-r border-slate-800 overflow-y-auto py-2 shrink-0">
            {HELP_MODULES.map((module) => (
              <button
                key={module.id}
                onClick={() => setActiveModuleId(module.id)}
                className={[
                  'w-full text-left px-4 py-3 transition-colors text-sm flex items-start gap-3',
                  activeModuleId === module.id
                    ? 'bg-slate-800/60 border-l-2 border-emerald-500 text-white'
                    : 'border-l-2 border-transparent text-slate-400 hover:text-white hover:bg-slate-800/30',
                ].join(' ')}
              >
                <span className="text-lg leading-none">{module.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{module.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                    {module.description}
                  </div>
                </div>
              </button>
            ))}
          </nav>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto p-6">
            <ModuleContent module={activeModule} />
          </div>
        </div>
      </div>
    </div>
  )
}

function ModuleContent({ module }: { module: HelpModule }) {
  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="flex items-center gap-3">
          <span className="text-3xl">{module.emoji}</span>
          <h3 className="text-2xl font-bold text-white">{module.title}</h3>
        </div>
        <p className="text-slate-400 text-sm mt-2">{module.description}</p>
      </div>

      <div className="space-y-5">
        {module.sections.map((section) => (
          <div key={section.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h4 className="font-semibold text-white text-base">{section.title}</h4>
            <p className="text-sm text-slate-300 mt-2 leading-relaxed">{section.blurb}</p>

            {section.steps && (
              <ul className="mt-3 space-y-1.5">
                {section.steps.map((step, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-slate-400 flex items-start gap-2 leading-relaxed"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.tips && (
              <div className="mt-3 space-y-1">
                {section.tips.map((tip, idx) => (
                  <div key={idx} className="text-xs text-slate-500 italic">
                    💡 {tip}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
